import React from 'react';
import { Check, X, QrCode, CalendarBlank, Clock, Users, CheckCircle, XCircle } from '@phosphor-icons/react';
import { getSymbol } from '../../utils/currency';

export const STATUS_CONFIG = {
  pending_payment: { label: 'Pending Payment', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  pending_approval: { label: 'Awaiting Approval', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  confirmed: { label: 'Confirmed', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  completed: { label: 'Completed', color: 'bg-slate-100 text-slate-700', dot: 'bg-slate-500' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-600', dot: 'bg-red-500' },
};

export const BookingRow = ({ booking, onAction, actionLoading, compact = false }) => {
  const statusCfg = STATUS_CONFIG[booking.status] || { label: booking.status, color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' };
  const isLoading = actionLoading === booking.booking_id;
  const canAccept = booking.status === 'pending_approval' || booking.status === 'pending_payment';
  const canReject = booking.status === 'pending_approval' || booking.status === 'pending_payment';
  const canComplete = booking.status === 'confirmed';

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return d; }
  };

  if (compact) {
    return (
      <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100" data-testid={`booking-${booking.booking_id}`}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-slate-900 text-sm truncate">{booking.buyer_name}</p>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${statusCfg.color}`}>{statusCfg.label}</span>
          </div>
          <p className="text-xs text-slate-500 truncate mt-0.5">{booking.listing_title} &middot; {getSymbol(booking.currency || booking.country)}{booking.total_price?.toLocaleString()}</p>
        </div>
        {(canAccept || canReject) && (
          <div className="flex items-center gap-1.5 ml-3 shrink-0">
            <button onClick={() => onAction(booking.booking_id, 'accept')} disabled={isLoading}
              className="w-8 h-8 bg-emerald-100 hover:bg-emerald-200 rounded-full flex items-center justify-center transition-colors"
              data-testid={`accept-${booking.booking_id}`} title="Accept">
              <Check size={14} className="text-emerald-600" weight="bold" />
            </button>
            <button onClick={() => onAction(booking.booking_id, 'reject')} disabled={isLoading}
              className="w-8 h-8 bg-red-100 hover:bg-red-200 rounded-full flex items-center justify-center transition-colors"
              data-testid={`reject-${booking.booking_id}`} title="Reject">
              <X size={14} className="text-red-600" weight="bold" />
            </button>
          </div>
        )}
        {canComplete && (
          <button onClick={() => onAction(booking.booking_id, 'complete')} disabled={isLoading}
            className="ml-3 px-3 py-1.5 bg-blue-100 hover:bg-blue-200 rounded-lg text-xs font-semibold text-blue-700 transition-colors shrink-0"
            data-testid={`complete-${booking.booking_id}`}>
            Complete
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition-shadow" data-testid={`booking-${booking.booking_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-slate-900">{booking.buyer_name}</p>
            <span className={`text-[11px] px-2.5 py-0.5 rounded-full font-semibold ${statusCfg.color}`}>{statusCfg.label}</span>
            {booking.checked_in && (
              <span className="text-[11px] px-2.5 py-0.5 rounded-full font-semibold bg-indigo-100 text-indigo-700 flex items-center gap-1"><QrCode size={12} weight="bold" /> Checked In</span>
            )}
          </div>
          <p className="text-sm text-slate-600 mt-1">{booking.listing_title}</p>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
            <span className="flex items-center gap-1"><CalendarBlank size={13} /> {formatDate(booking.start_date)}{booking.end_date !== booking.start_date ? ` - ${formatDate(booking.end_date)}` : ''}</span>
            {booking.start_time && <span className="flex items-center gap-1"><Clock size={13} /> {booking.start_time} - {booking.end_time}</span>}
            {booking.guests > 1 && <span className="flex items-center gap-1"><Users size={13} /> {booking.guests} guests</span>}
            <span className="flex items-center gap-1 font-semibold text-slate-700">{getSymbol(booking.currency || booking.country)}{booking.total_price?.toLocaleString()}</span>
          </div>

          {booking.add_on_details && booking.add_on_details.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {booking.add_on_details.map(a => (
                <span key={a.key} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{a.label}</span>
              ))}
            </div>
          )}

          {booking.notes && (
            <p className="text-xs text-slate-400 mt-2 italic">Note: {booking.notes}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 shrink-0">
          {canAccept && (
            <button onClick={() => onAction(booking.booking_id, 'accept')} disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
              data-testid={`accept-${booking.booking_id}`}>
              <CheckCircle size={14} weight="bold" /> Accept
            </button>
          )}
          {canReject && (
            <button onClick={() => onAction(booking.booking_id, 'reject')} disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-red-200 hover:bg-red-50 text-red-600 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
              data-testid={`reject-${booking.booking_id}`}>
              <XCircle size={14} weight="bold" /> Reject
            </button>
          )}
          {canComplete && (
            <button onClick={() => onAction(booking.booking_id, 'complete')} disabled={isLoading}
              className="flex items-center gap-1.5 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
              data-testid={`complete-${booking.booking_id}`}>
              <CheckCircle size={14} weight="bold" /> Complete
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
